import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { PageImageLightboxProvider } from 'src/common/components/PageImageLightboxContext';
import DashboardListPageLayout from 'src/common/components/dashboard/DashboardListPageLayout';
import ListFilterEmptyState from 'src/common/components/dashboard/ListFilterEmptyState';
import {
  DashboardPageToolbar,
  DashboardToolbarActions,
  DashboardToolbarAside,
  dashboardPrimaryActionButtonProps,
} from 'src/common/components/DashboardPageToolbar';
import PageBox from 'src/common/components/PageBox';
import { hasActiveListFilter, hasActiveSelectFilter } from 'src/helpers/listFilters';
import { useConfirm } from 'material-ui-confirm';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import useTitle from 'react-use/lib/useTitle';
import { PageImage } from 'src/helpers/pageImageUtils';
import { UploadedFile } from 'src/interfaces/FileManager';
import useFileManagerStore from 'src/stores/fileManagerStore';

import usePersistentView from 'src/hooks/usePersistentView';
import { AppFileRouterEndpoint } from 'src/interfaces/AppFileRouter';
import { EView } from 'src/interfaces/View';

import EditMetadataDialog from './components/EditMetadataDialog';
import FileCard from './components/FileCard';
import FileListDropOverlay from './components/FileListDropOverlay';
import FileManagerEmptyState from './components/FileManagerEmptyState';
import FileTypeTabs from './components/FileTypeTabs';
import FilesTable from './components/FilesTable';
import MediaManagerToolbar from './components/MediaManagerToolbar';
import { UPLOAD_ENDPOINTS } from './components/resolveUploadEndpoint';
import UploadDialog from './components/UploadDialog';

type FileManagerViewProps = {
  view?: EView;
  onSetView?: (view: EView) => void;
};

interface MediaManagerContentProps extends FileManagerViewProps {
  mode?: 'manage' | 'select';
  endpoint?: 'all' | AppFileRouterEndpoint;
  onSelect?: (file: UploadedFile) => void;
  multiple?: boolean;
  selectedFileIds?: string[];
  showTitle?: boolean;
  hideToolbar?: boolean;
  allowUpload?: boolean;
}

const FileManager = ({
  mode = 'manage',
  endpoint: initialEndpoint = 'all',
  onSelect = () => {},
  multiple = false,
  selectedFileIds = [],
  showTitle = true,
  hideToolbar = false,
  allowUpload: allowUploadProp,
  view: controlledView,
  onSetView: controlledSetView,
}: MediaManagerContentProps) => {
  const { t } = useTranslation();
  useTitle(
    showTitle ? `${t('system.fileManager.pageTitle')} - ${t('base.defaultTitle')}` : t('base.defaultTitle'),
  );
  const confirm = useConfirm();
  const files = useFileManagerStore(state => state.files);
  const currentPage = useFileManagerStore(state => state.currentPage);
  const totalPages = useFileManagerStore(state => state.totalPages);
  const loading = useFileManagerStore(state => state.loading);
  const storeEndpoint = useFileManagerStore(state => state.endpoint);
  const search = useFileManagerStore(state => state.search);
  const isUsedFilter = useFileManagerStore(state => state.isUsedFilter);
  const setCurrentPage = useFileManagerStore(state => state.setCurrentPage);
  const deleteFile = useFileManagerStore(state => state.deleteFile);
  const setEndpointFilter = useFileManagerStore(state => state.setEndpointFilter);

  const [internalView, setInternalView] = usePersistentView();
  const view = controlledView ?? internalView;
  const setView = controlledSetView ?? setInternalView;
  const [editingFile, setEditingFile] = useState<UploadedFile | null>(null);
  const [isUploadOpen, setUploadOpen] = useState(false);

  const lockedEndpoint = initialEndpoint !== 'all' ? initialEndpoint : undefined;
  const allowUpload = allowUploadProp ?? mode === 'manage';

  const allowedUploadEndpoints = useMemo(
    () => (lockedEndpoint ? [lockedEndpoint] : UPLOAD_ENDPOINTS),
    [lockedEndpoint],
  );

  const uploadInitialEndpoint = useMemo(() => {
    if (lockedEndpoint) {
      return lockedEndpoint;
    }
    if (storeEndpoint !== 'all') {
      return storeEndpoint;
    }
    return 'image';
  }, [lockedEndpoint, storeEndpoint]);

  const hasActiveFilters =
    hasActiveListFilter(search) ||
    hasActiveSelectFilter(isUsedFilter) ||
    (!lockedEndpoint && hasActiveSelectFilter(storeEndpoint));

  useEffect(() => {
    if (initialEndpoint !== 'all') {
      setEndpointFilter(initialEndpoint);
    }
  }, [initialEndpoint, setEndpointFilter]);

  const handlePageChange = (_event: ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const showPagination = !loading && files.length > 0;

  const lightboxImages = useMemo((): PageImage[] => {
    return files
      .filter(file => file.uploadEndpoint === 'image')
      .map(file => ({
        src: file.fileUrl,
        alt: file.altText || file.fileName,
      }));
  }, [files]);

  const renderPagination = (boxSx?: SxProps<Theme>) =>
    showPagination ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', ...boxSx }}>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          disabled={loading}
        />
      </Box>
    ) : null;

  const handleDelete = async (fileId: string) => {
    const { confirmed } = await confirm({
      title: t('system.fileManager.deleteConfirm'),
      description: t('system.fileManager.deleteDescription'),
      confirmationText: t('base.delete'),
      cancellationText: t('base.cancel'),
    });
    if (!confirmed) {
      return;
    }
    await deleteFile(fileId);
  };

  const handleEdit = (file: UploadedFile) => {
    setEditingFile(file);
  };

  const isEmbedded = hideToolbar;

  const uploadButton = allowUpload ? (
    <Button
      {...dashboardPrimaryActionButtonProps()}
      startIcon={<AddIcon />}
      onClick={() => setUploadOpen(true)}
    >
      {t('form.upload.label')}
    </Button>
  ) : undefined;

  const fileListContent = (
    <>
      {!loading && files.length === 0 ? (
        hasActiveFilters ? (
          <ListFilterEmptyState />
        ) : (
          <FileManagerEmptyState
            onUpload={allowUpload ? () => setUploadOpen(true) : undefined}
          />
        )
      ) : files.length > 0 ? (
        <>
          {view === EView.grid ? (
            <Grid container spacing={2} sx={{ width: '100%', minWidth: 0 }}>
              {files.map(file => (
                <FileCard
                  key={file.id}
                  file={file}
                  mode={mode}
                  multiple={multiple}
                  selected={selectedFileIds.includes(file.id)}
                  onSelect={onSelect}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                />
              ))}
            </Grid>
          ) : (
            <FilesTable
              files={files}
              mode={mode}
              multiple={multiple}
              selectedFileIds={selectedFileIds}
              onSelect={onSelect}
              onDelete={handleDelete}
              onEdit={handleEdit}
            />
          )}

          {renderPagination({ mt: 4, pb: 2 })}
        </>
      ) : null}
    </>
  );

  const fileTypeTabs = <FileTypeTabs lockedEndpoint={lockedEndpoint} />;

  const wrappedFileList = (
    <PageImageLightboxProvider images={lightboxImages}>
      <FileListDropOverlay
        enabled={allowUpload}
        lockedEndpoint={lockedEndpoint}
        activeEndpoint={storeEndpoint}
      >
        {fileListContent}
      </FileListDropOverlay>
    </PageImageLightboxProvider>
  );

  const dialogs = (
    <>
      <EditMetadataDialog
        open={!!editingFile}
        file={editingFile}
        onClose={() => setEditingFile(null)}
      />
      <UploadDialog
        open={isUploadOpen}
        onClose={() => setUploadOpen(false)}
        initialEndpoint={uploadInitialEndpoint}
        allowedEndpoints={allowedUploadEndpoints}
      />
    </>
  );

  if (showTitle) {
    return (
      <>
        <DashboardListPageLayout
          title={t('system.fileManager.pageTitle')}
          headerActions={uploadButton}
          toolbar={hideToolbar ? undefined : <MediaManagerToolbar view={view} onSetView={setView} />}
          isLoading={loading && files.length === 0}
          scrollable
        >
          {fileTypeTabs}
          {wrappedFileList}
        </DashboardListPageLayout>
        {dialogs}
      </>
    );
  }

  const contentBoxSx = isEmbedded
    ? {
        px: 2,
        pt: 1,
        pb: 2,
        overflowX: 'hidden',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }
    : undefined;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {hideToolbar ? null : (
        <DashboardPageToolbar>
          {allowUpload ? (
            <DashboardToolbarActions>{uploadButton}</DashboardToolbarActions>
          ) : null}
          <DashboardToolbarAside variant="grow">
            <MediaManagerToolbar view={view} onSetView={setView} />
          </DashboardToolbarAside>
        </DashboardPageToolbar>
      )}
      {fileTypeTabs}
      {isEmbedded ? renderPagination({ py: 1.5, flexShrink: 0 }) : null}
      <PageBox fill scrollable isLoading={loading && files.length === 0} sx={contentBoxSx}>
        {wrappedFileList}
      </PageBox>
      {dialogs}
    </Box>
  );
};

export default FileManager;
